/**
 * Uniform grid hash over an interleaved XYZ `Float64Array` for fixed-radius
 * neighbour queries. Simpler than a KD-tree, allocation-light, and plenty fast
 * for the local PCA normal estimation (a few thousand points in the seed box).
 *
 * Points are stored once in the typed array; the index only holds integer
 * offsets bucketed by cell, so building it is O(N) with no per-point objects.
 *
 * Coordinates are large (UTM-scale). We bucket relative to the array's min
 * corner so cell indices stay small integers regardless of world offset.
 */

export class GridIndex {
  private readonly positions: Float64Array;
  private readonly cell: number;
  private readonly invCell: number;
  private readonly minX: number;
  private readonly minY: number;
  private readonly minZ: number;
  private readonly nx: number;
  private readonly ny: number;
  private readonly nz: number;
  /** Bucket → list of point indices (index i means xyz at positions 3i..3i+2). */
  private readonly buckets: Map<number, number[]> = new Map();

  constructor(positions: Float64Array, count: number, cellSize: number) {
    this.positions = positions;
    this.cell = cellSize > 0 ? cellSize : 1;
    this.invCell = 1 / this.cell;

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < count; i++) {
      const x = positions[3 * i];
      const y = positions[3 * i + 1];
      const z = positions[3 * i + 2];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
    }
    this.minX = minX;
    this.minY = minY;
    this.minZ = minZ;
    this.nx = Math.max(1, Math.ceil((maxX - minX) * this.invCell) + 1);
    this.ny = Math.max(1, Math.ceil((maxY - minY) * this.invCell) + 1);
    this.nz = Math.max(1, Math.ceil((maxZ - minZ) * this.invCell) + 1);

    for (let i = 0; i < count; i++) {
      const key = this.cellKey(positions[3 * i], positions[3 * i + 1], positions[3 * i + 2]);
      let bucket = this.buckets.get(key);
      if (!bucket) {
        bucket = [];
        this.buckets.set(key, bucket);
      }
      bucket.push(i);
    }
  }

  private cellKey(x: number, y: number, z: number): number {
    const ix = Math.floor((x - this.minX) * this.invCell);
    const iy = Math.floor((y - this.minY) * this.invCell);
    const iz = Math.floor((z - this.minZ) * this.invCell);
    // Pack into one integer key. nx/ny/nz bound each axis so this is unique.
    return (iz * this.ny + iy) * this.nx + ix;
  }

  /**
   * Collect indices of points within `radius` of (x,y,z) into `out` (cleared
   * first). Returns the count written. Scans the 3×3×3 block of cells around
   * the query when `radius ≤ cellSize`; for larger radii pass a `cellSize`
   * sized to the query at construction time.
   */
  queryRadius(x: number, y: number, z: number, radius: number, out: number[]): number {
    out.length = 0;
    const r2 = radius * radius;
    const reach = Math.max(1, Math.ceil(radius * this.invCell));
    const cx = Math.floor((x - this.minX) * this.invCell);
    const cy = Math.floor((y - this.minY) * this.invCell);
    const cz = Math.floor((z - this.minZ) * this.invCell);
    const p = this.positions;
    for (let dz = -reach; dz <= reach; dz++) {
      const iz = cz + dz;
      if (iz < 0 || iz >= this.nz) continue;
      for (let dy = -reach; dy <= reach; dy++) {
        const iy = cy + dy;
        if (iy < 0 || iy >= this.ny) continue;
        for (let dx = -reach; dx <= reach; dx++) {
          const ix = cx + dx;
          if (ix < 0 || ix >= this.nx) continue;
          const bucket = this.buckets.get((iz * this.ny + iy) * this.nx + ix);
          if (!bucket) continue;
          for (let b = 0; b < bucket.length; b++) {
            const j = bucket[b];
            const ddx = p[3 * j] - x;
            const ddy = p[3 * j + 1] - y;
            const ddz = p[3 * j + 2] - z;
            if (ddx * ddx + ddy * ddy + ddz * ddz <= r2) out.push(j);
          }
        }
      }
    }
    return out.length;
  }
}
